import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Client } from '../models/table-models';
import { NewRequestBranch } from '../models/dashboard-models';
import { defaultSelectedBranch, defaultSelectedClient } from '../utils/Enums/NewRequest';
import { normalizeRequestId, persistRequestSelection, readRequestSelection, RequestSelection } from '../utils/request-selection';
import { useRequestEntities } from '../utils/useRequestEntities';
import { useRequestResources } from '../utils/useRequestResources';

interface NewContextData extends ReturnType<typeof useRequestResources> {
    selectedClientId?: number;
    selectedBranchId?: number;
    isLoadingSelection: boolean;
    selectionLoadFailed: boolean;
    retrySelection: () => void;
    selectedClient: Client;
    selectedBranch: NewRequestBranch;
    newBranch: NewRequestBranch;
    onClientSelect: (option?: Partial<Client> | null) => void;
    onBranchSelect: (option?: Partial<NewRequestBranch> | null) => void;
    updateClientAfterSave: (client: Client) => void;
    updateBranchAfterSave: (branch: NewRequestBranch) => void;
    clearSelection: () => void;
    onLocationClick: (lat: string, lng: string) => void;
    refetchTasks?: () => void;
    setshowOnlyClient: React.Dispatch<React.SetStateAction<boolean>>;
    showOnlyClient: boolean;
}

export const NewRequestContext = createContext<NewContextData>({} as NewContextData);

export const NewRequestProvider = ({ children, refetchTasks }: { children: ReactNode; refetchTasks?: () => void }) => {
    const [selection, setSelection] = useState(readRequestSelection);
    const selectionRef = useRef(selection);
    const [newBranch, setNewBranch] = useState(defaultSelectedBranch);
    const [showOnlyClient, setshowOnlyClient] = useState(false);
    const { client, branch, updateClient, updateBranch, loadFailed, retry } = useRequestEntities(selection.clientId, selection.branchId);
    const resources = useRequestResources(selection.clientId, selection.branchId);

    const select = useCallback((next: RequestSelection) => {
        selectionRef.current = next;
        setSelection(next);
        persistRequestSelection(next);
    }, []);

    const onClientSelect = useCallback((option?: Partial<Client> | null) => {
        const clientId = normalizeRequestId(option?.id);
        if (option && clientId) updateClient({ ...defaultSelectedClient, ...option });
        const current = selectionRef.current;
        select({ clientId, branchId: current.clientId === clientId ? current.branchId : undefined });
    }, [select, updateClient]);

    const onBranchSelect = useCallback((option?: Partial<NewRequestBranch> | null) => {
        const branchId = normalizeRequestId(option?.id);
        if (option && branchId) updateBranch({ ...defaultSelectedBranch, ...option });
        select({ ...selectionRef.current, branchId });
    }, [select, updateBranch]);

    const clearSelection = useCallback(() => {
        select({});
        setNewBranch(defaultSelectedBranch);
        setshowOnlyClient(false);
        ['storedBranches', 'slectedCurrentBranch', 'isModalOpen'].forEach(key => localStorage.removeItem(key));
    }, [select]);

    // Legacy map consumers still read snapshots; restoration uses only the IDs above.
    useEffect(() => {
        if (client) localStorage.setItem('selectedClient', JSON.stringify(client));
    }, [client]);
    useEffect(() => {
        if (branch) {
            localStorage.setItem('selectedBranch', JSON.stringify(branch));
            localStorage.setItem('markerID', JSON.stringify(branch.id));
        }
    }, [branch]);

    const onLocationClick = useCallback((latitude: string, longitude: string) => {
        setNewBranch(current => ({ ...current, location: { ...current.location, latitude, longitude } }));
    }, []);

    return (
        <NewRequestContext.Provider value={{
            ...resources,
            selectedClientId: selection.clientId,
            selectedBranchId: selection.branchId,
            isLoadingSelection: Boolean((selection.clientId && !client) || (selection.branchId && !branch)),
            selectionLoadFailed: loadFailed,
            retrySelection: retry,
            selectedClient: client ?? defaultSelectedClient,
            selectedBranch: branch ?? (selection.branchId ? defaultSelectedBranch : newBranch),
            newBranch, onClientSelect, onBranchSelect,
            updateClientAfterSave: updateClient, updateBranchAfterSave: updateBranch,
            clearSelection, onLocationClick, refetchTasks, showOnlyClient, setshowOnlyClient,
        }}>
            {children}
        </NewRequestContext.Provider>
    );
};
